import React from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

/**
 * PayPalCheckout — reusable Smart Buttons wrapper.
 *
 * Props:
 *  - purpose: "deepdive" | "donation"
 *  - getPayload(): returns extra data sent to /paypal/create-order
 *      For deepdive: { branch, jobs }
 *      For donation: { amount }
 *  - onPaid(captureResult): called when capture succeeds — receives { paid, report_id? }
 *  - onError(err): optional error handler
 *  - disabled: boolean — disables the buttons
 */
export default function PayPalCheckout({ purpose, getPayload, onPaid, onError, disabled }) {
  if (!CLIENT_ID) {
    return (
      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
        PayPal is not configured (missing client id).
      </div>
    );
  }

  return (
    <div className={disabled ? "pointer-events-none opacity-50" : ""} data-testid={`paypal-buttons-${purpose}`}>
      <PayPalScriptProvider
        options={{
          clientId: CLIENT_ID,
          currency: "USD",
          intent: "capture",
        }}
      >
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", label: purpose === "donation" ? "donate" : "pay" }}
          disabled={disabled}
          createOrder={async () => {
            try {
              const payload = (await getPayload?.()) || {};
              const { data } = await axios.post(`${API}/paypal/create-order`, { purpose, ...payload });
              return data.order_id;
            } catch (err) {
              onError?.(err);
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await axios.post(`${API}/paypal/capture-order`, {
                order_id: data.orderID,
                purpose,
              });
              onPaid?.(res.data);
            } catch (err) {
              onError?.(err);
            }
          }}
          onError={(err) => onError?.(err)}
          onCancel={() => onError?.({ canceled: true })}
        />
      </PayPalScriptProvider>
    </div>
  );
}
